import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
  getCardgames, 
  getTournamentFormatsForReports, 
  getTournamentEventCategories,
  getTournamentTypes,
  getGameSetsForReports,
  createTournament 
} from '../../services/supabaseService';

export const CreateTournament: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  // Auxiliary data
  const [games, setGames] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [formats, setFormats] = useState<any[]>([]);
  const [tournamentTypes, setTournamentTypes] = useState<any[]>([]);
  const [gameSets, setGameSets] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadingSets, setLoadingSets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Helper to generate current local datetime in YYYY-MM-DDTHH:mm format
  const getNowLocalDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper to generate an 8-character unique alphanumeric ticket
  const generateTicketCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let res = '';
    for (let i = 0; i < 8; i++) {
      res += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return res;
  };

  const [formData, setFormData] = useState({
    name: '',
    cardgame_id: '',
    event_category_id: '1',
    format_id: '1',
    tournament_type_id: '1',
    tourney_set_id: '',
    starts_at: getNowLocalDateTime(),
    ends_at: '',
    max_players: 16,
    online: false,
    decklist_required: false,
    ticket: generateTicketCode(),
    status: 'open',
    description: '',
    // Ticket / Venda integrada - todo torneio possui ingresso associado
    has_ticket: true,
    ticket_price: 0,
    max_quantity: 16,
    max_per_user: 1,
    is_active: true,
    // Legacy / Winners
    top1: '',
    top2: '',
    top3: ''
  });

  // Load initial options
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [gamesData, catsData, typesData] = await Promise.all([
          getCardgames(),
          getTournamentEventCategories(),
          getTournamentTypes()
        ]);

        setGames(gamesData || []);
        setCategories(catsData || []);
        setTournamentTypes(typesData || []);

        const initialGameId = gamesData?.[0]?.id ? String(gamesData[0].id) : '1';
        const formatsData = await getTournamentFormatsForReports(initialGameId);
        setFormats(formatsData || []);

        setFormData(prev => ({
          ...prev,
          cardgame_id: initialGameId,
          event_category_id: catsData?.[0]?.id ? String(catsData[0].id) : '1',
          tournament_type_id: typesData?.[0]?.id ? String(typesData[0].id) : '1',
          format_id: formatsData?.[0]?.id ? String(formatsData[0].id) : '1'
        }));

        // Fetch sets for first game
        if (initialGameId) {
          const sets = await getGameSetsForReports(initialGameId);
          setGameSets(sets || []);
          if (sets && sets.length > 0) {
            setFormData(prev => ({ ...prev, tourney_set_id: String(sets[0].id) }));
          }
        }
      } catch (error) {
        console.error('Error fetching initial tournament setup data:', error);
        showNotification("Erro ao carregar dados auxiliares", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // When cardgame changes, reload formats and sets
  const handleGameChange = async (gameId: string) => {
    setFormData(prev => ({ ...prev, cardgame_id: gameId, tourney_set_id: '' }));
    setLoadingSets(true);
    try {
      const [newFormats, newSets] = await Promise.all([
        getTournamentFormatsForReports(gameId),
        getGameSetsForReports(gameId)
      ]);

      setFormats(newFormats || []);
      setGameSets(newSets || []);

      setFormData(prev => ({
        ...prev,
        format_id: newFormats?.[0]?.id ? String(newFormats[0].id) : prev.format_id,
        tourney_set_id: newSets?.[0]?.id ? String(newSets[0].id) : ''
      }));
    } catch (err) {
      console.warn('Error fetching game-specific sets/formats:', err);
    } finally {
      setLoadingSets(false);
    }
  };

  const handleRegenerateTicket = () => {
    setFormData(prev => ({ ...prev, ticket: generateTicketCode() }));
    showNotification("Novo código de ticket gerado!", "info");
  };

  const handleMaxPlayersQuickSelect = (num: number) => {
    setFormData(prev => ({ 
      ...prev, 
      max_players: num,
      max_quantity: (prev.max_quantity === prev.max_players || prev.max_quantity > num) ? num : prev.max_quantity
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showNotification("Por favor, informe o nome do torneio.", "error");
      return;
    }

    if (!formData.starts_at) {
      showNotification("Por favor, defina a data e horário de início.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const tournamentCapacity = Math.max(2, Number(formData.max_players) || 8);

      const res: any = await createTournament({
        name: formData.name.trim(),
        cardgame_id: formData.cardgame_id ? Number(formData.cardgame_id) : null,
        event_category_id: formData.event_category_id ? Number(formData.event_category_id) : null,
        format_id: formData.format_id ? Number(formData.format_id) : null,
        tournament_type_id: formData.tournament_type_id ? Number(formData.tournament_type_id) : null,
        tourney_set_id: formData.tourney_set_id ? Number(formData.tourney_set_id) : null,
        starts_at: formData.starts_at,
        ends_at: null,
        max_players: tournamentCapacity,
        online: Boolean(formData.online),
        decklist_required: Boolean(formData.decklist_required),
        ticket: formData.ticket?.trim().toUpperCase(),
        status: formData.status || 'open',
        description: formData.description?.trim() || null,
        // Quantidade de ingressos colocados à venda é SEMPRE idêntica ao número de vagas do torneio
        has_ticket: true,
        ticket_price: Number(formData.ticket_price) || 0,
        max_quantity: tournamentCapacity,
        ticket_quantity: tournamentCapacity,
        max_per_user: Math.max(1, Number(formData.max_per_user) || 1),
        is_active: formData.is_active !== false,
        top1: formData.top1 || null,
        top2: formData.top2 || null,
        top3: formData.top3 || null
      });

      if (res && !res.error) {
        if (res.ticket_error) {
          showNotification(`Torneio criado! Aviso do ingresso: ${res.ticket_error}`, "info");
        } else {
          showNotification("Torneio criado e produto de ingresso associado com sucesso!", "success");
        }
        navigate('/meus-torneios');
      } else {
        showNotification(res?.error || "Erro ao criar torneio.", "error");
      }
    } catch (error: any) {
      console.error('Error creating tournament:', error);
      showNotification(error?.message || "Erro ao criar torneio.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-4">
        <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400">Carregando configurações do evento...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center space-x-2 text-xs font-bold text-slate-400">
            <Link to="/meus-torneios" className="hover:text-purple-400 transition-colors flex items-center space-x-1">
              <i className="fas fa-chevron-left text-[10px]"></i>
              <span>Meus Torneios</span>
            </Link>
            <span>/</span>
            <span className="text-purple-400 uppercase tracking-wider">Novo Evento</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white italic tracking-tighter uppercase flex items-center space-x-3">
            <i className="fas fa-trophy text-purple-400"></i>
            <span>Criar Novo Torneio</span>
          </h1>
        </div>

        <Link
          to="/meus-torneios"
          className="self-start sm:self-auto px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
        >
          Cancelar
        </Link>
      </div>

      {/* Formulário Único */}
      <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
          
          {/* Coluna Principal: Informações do Torneio */}
          <div className="lg:col-span-8 p-6 sm:p-8 space-y-5">
            
            {/* Nome do Torneio */}
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                Nome do Torneio <strong className="text-purple-400">*</strong>
              </label>
              <input 
                required
                type="text" 
                placeholder="Ex: Torneio Semanal One Piece, Store Championship..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 font-semibold"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Grid: Jogo & Categoria */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Jogo / TCG <strong className="text-purple-400">*</strong>
                </label>
                <div className="relative">
                  <select 
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.cardgame_id}
                    onChange={(e) => handleGameChange(e.target.value)}
                  >
                    {games.map(game => (
                      <option key={game.id} value={game.id}>{game.name}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Categoria do Evento
                </label>
                <div className="relative">
                  <select 
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.event_category_id}
                    onChange={(e) => setFormData({ ...formData, event_category_id: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>
            </div>

            {/* Grid: Formato, Estrutura e Set */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Formato
                </label>
                <div className="relative">
                  <select 
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.format_id}
                    onChange={(e) => setFormData({ ...formData, format_id: e.target.value })}
                  >
                    {formats.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Estrutura
                </label>
                <div className="relative">
                  <select 
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.tournament_type_id}
                    onChange={(e) => setFormData({ ...formData, tournament_type_id: e.target.value })}
                  >
                    {tournamentTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Modalidade
                </label>
                <div className="relative">
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.online ? 'online' : 'presencial'}
                    onChange={(e) => setFormData({ ...formData, online: e.target.value === 'online' })}
                  >
                    <option value="presencial">Presencial</option>
                    <option value="online">Online</option>
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              
            </div>

            {/* Grid: Modalidade, Decklist e Início */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  <span>Set</span>
                  {loadingSets && <span className="text-[10px] text-purple-400">carregando...</span>}
                </label>
                <div className="relative">
                  <select 
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.tourney_set_id}
                    onChange={(e) => setFormData({ ...formData, tourney_set_id: e.target.value })}
                  >
                    <option value="">Geral / Não especificado</option>
                    {gameSets.map(set => (
                      <option key={set.id} value={set.id}>
                        {set.name} {set.code ? `(${set.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Decklist
                </label>
                <div className="relative">
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                    value={formData.decklist_required ? 'required' : 'optional'}
                    onChange={(e) => setFormData({ ...formData, decklist_required: e.target.value === 'required' })}
                  >
                    <option value="optional">Não obrigatória</option>
                    <option value="required">Obrigatória</option>
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Início <strong className="text-purple-400">*</strong>
                </label>
                <input 
                  required
                  type="datetime-local" 
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 font-medium"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                />
              </div>
            </div>

            {/* Grid: Vagas, Status Inicial e Código do Ticket */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Vagas <strong className="text-purple-400">*</strong>
                </label>
                <input 
                  required
                  type="number" 
                  min="2"
                  max="1024"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 font-medium"
                  value={formData.max_players}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 8;
                    setFormData(prev => ({ 
                      ...prev, 
                      max_players: val, 
                      max_quantity: val 
                    }));
                  }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Status Inicial
                </label>
                <div className="relative">
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none font-medium cursor-pointer"
                  >
                    <option value="open">Inscrições Abertas</option>
                    <option value="scheduled">Agendado</option>
                  </select>
                  <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs"></i>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Código do Ticket
                </label>
                <input 
                  type="text" 
                  readOnly
                  tabIndex={-1}
                  title="Código gerado automaticamente"
                  className="w-full bg-slate-950/70 border border-slate-800/80 rounded-xl px-4 py-2.5 text-purple-400/90 font-mono font-bold text-sm uppercase tracking-widest select-all cursor-default focus:outline-none"
                  value={formData.ticket}
                />
              </div>
            </div>

            {/* Descrição & Premiação */}
            <div className="space-y-1.5 pt-1">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                Descrição & Premiação (Opcional)
              </label>
              <textarea 
                rows={3}
                placeholder="Premiação, regulamento ou orientações para os participantes..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 resize-none leading-relaxed"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

          </div>

          {/* Coluna Lateral: Ingresso e Publicação */}
          <div className="lg:col-span-4 p-6 sm:p-8 bg-slate-950/40 flex flex-col justify-between space-y-6">
            <div className="space-y-5">
              
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-ticket-alt text-purple-400 text-xs"></i>
                    <h3 className="text-xs font-black text-white uppercase tracking-wider">
                      Ingresso do Torneio
                    </h3>
                  </div>
                  <span className="text-[11px] font-bold text-slate-400">
                    <strong className="text-purple-400">{formData.max_players || 8}</strong> ingressos
                  </span>
                </div>

                <div className="space-y-3.5 pt-1">
                  {/* Valor da Inscrição */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                      Valor da Inscrição
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-xs">R$</span>
                      <input 
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00 (Gratuito)"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-3 py-2 text-emerald-400 font-bold text-sm focus:outline-none focus:border-emerald-500"
                        value={formData.ticket_price || ''}
                        onChange={(e) => setFormData({ ...formData, ticket_price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Limite por Jogador & Status Venda */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                        Limite / Jogador
                      </label>
                      <select
                        value={formData.max_per_user}
                        onChange={(e) => setFormData({ ...formData, max_per_user: Number(e.target.value) })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-white text-xs font-semibold focus:outline-none focus:border-purple-500 cursor-pointer"
                      >
                        <option value={1}>1 por jogador</option>
                        <option value={2}>2 por jogador</option>
                        <option value={4}>4 por jogador</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                        Vendas na Loja
                      </label>
                      <select
                        value={formData.is_active ? 'active' : 'paused'}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-white text-xs font-semibold focus:outline-none focus:border-purple-500 cursor-pointer"
                      >
                        <option value="active">Ativas</option>
                        <option value="paused">Pausadas</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Botões de Ação na Coluna Lateral */}
            <div className="pt-4 space-y-2 border-t border-slate-800/80">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center justify-center space-x-2 cursor-pointer"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Criando Torneio...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-trophy text-xs"></i>
                    <span>Publicar Torneio</span>
                  </>
                )}
              </button>

              <Link
                to="/meus-torneios"
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors text-center block"
              >
                Cancelar
              </Link>
            </div>

          </div>

        </div>
      </form>
    </div>
  );
};

